import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Button,
  Center,
  Divider,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import {
  RemoveFromMessageThreadDryRunOutputFragment,
  RemoveFromMessageThreadDryRunQuery,
  RemoveFromMessageThreadDryRunQueryVariables,
  RemoveFromMessageThreadMutation,
  RemoveFromMessageThreadMutationVariables,
  UserRoleType,
} from "gql/graphql";
import REMOVE_FROM_MESSAGE_THREAD_DRY_RUN_QUERY from "./networking/remove-from-message-thread-dry-run";
import { useUsers } from "modules/providers/users-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import useBurstQuery from "modules/hooks/use-burst-query";
import { useAuth } from "modules/providers/auth-provider";
import UserName from "modules/_shared/user-name";
import {
  getDistributionGroupMention,
  getUserMention,
} from "modules/utils/Functions";
import colors from "app/colors";
import UserAvatar from "modules/_shared/user-avatar";
import { useMutation } from "@apollo/client";
import REMOVE_FROM_MESSAGE_THREAD_MUTATION from "./networking/remove-from-message-thread";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import { InfoIcon, RefreshIcon, PersonOutlineIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  messageThreadId: string;
  userAndDistributionGroupIds: string[];
}

const ConfirmRemoveUsersGroupsModal = ({
  messageThreadId,
  userAndDistributionGroupIds,
}: IProps) => {
  const { me } = useAuth();
  const { closeModal } = useOverlay();
  const { getUsersById, usersById } = useUsers();
  const { track } = useMixpanel();
  const { getDistributionGroupsById, distributionGroupsById } =
    useDistributionGroups();
  const [displayType, setDisplayType] = useState<"LOADING" | "MODAL" | "ERROR">(
    "LOADING",
  );
  const [error, setError] = useState<ReactNode>();
  const [isDryRunMismatch, setIsDryRunMismatch] = useState(false);

  const userIdListToRemove = getUsersById(userAndDistributionGroupIds).map(
    (user) => user.id,
  );

  const distributionGroupIdListToRemove = getDistributionGroupsById(
    userAndDistributionGroupIds,
  ).map((distributionGroup) => distributionGroup.id);

  const user =
    userIdListToRemove.length === 1
      ? usersById[userIdListToRemove[0]]
      : undefined;

  const distributionGroup =
    distributionGroupIdListToRemove.length === 1
      ? distributionGroupsById[distributionGroupIdListToRemove[0]]
      : undefined;

  const [dryRunOutput, setDryRunOutput] =
    useState<RemoveFromMessageThreadDryRunOutputFragment>();

  const [removeFromMessageThreadDryRunQueryAPI] = useBurstQuery<
    RemoveFromMessageThreadDryRunQuery,
    RemoveFromMessageThreadDryRunQueryVariables
  >(REMOVE_FROM_MESSAGE_THREAD_DRY_RUN_QUERY);

  const [removeFromMessageThreadDryRunMutationAPI] = useMutation<
    RemoveFromMessageThreadMutation,
    RemoveFromMessageThreadMutationVariables
  >(REMOVE_FROM_MESSAGE_THREAD_MUTATION);

  useEffect(() => {
    executeDryRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const willRemoveMe = useMemo(() => {
    if (!me) return false;
    return dryRunOutput?.removedUserIdList.includes(me.id);
  }, [dryRunOutput?.removedUserIdList, me]);

  const willRemoveExternalContacts = useMemo(() => {
    if (!dryRunOutput) {
      return false;
    }
    return (
      getUsersById(dryRunOutput.removedUserIdList).filter(
        (user) => user.roleType === UserRoleType.Email,
      ).length > 0
    );
  }, [dryRunOutput, getUsersById]);

  const isRemovingSingleUser = useMemo(
    () =>
      userIdListToRemove.length === 1 &&
      distributionGroupIdListToRemove.length === 0,
    [distributionGroupIdListToRemove.length, userIdListToRemove.length],
  );

  const willRemoveAdditionalGroups = useMemo(() => {
    if (!dryRunOutput) {
      return false;
    }
    return (
      isRemovingSingleUser &&
      dryRunOutput.removedDistributionGroupIdList.length > 0
    );
  }, [dryRunOutput, isRemovingSingleUser]);

  const isRemovingSingleDistributionGroup = useMemo(
    () =>
      userIdListToRemove.length === 0 &&
      distributionGroupIdListToRemove.length === 1,
    [distributionGroupIdListToRemove.length, userIdListToRemove.length],
  );

  const isRemovingSingleExternalUser = useMemo(
    () =>
      isRemovingSingleUser &&
      getUsersById(userIdListToRemove)[0]?.roleType === UserRoleType.Email,
    [getUsersById, isRemovingSingleUser, userIdListToRemove],
  );

  const isRemovingOnlyMe = useMemo(
    () => isRemovingSingleUser && userIdListToRemove[0] === me?.id,
    [isRemovingSingleUser, userIdListToRemove, me?.id],
  );

  const actionType = useMemo(() => {
    if (isRemovingOnlyMe) {
      return "REMOVING_ME";
    }
    if (isRemovingSingleExternalUser) {
      return "REMOVING_EXTERNAL_USER";
    }
    if (isRemovingSingleDistributionGroup) {
      return "REMOVING_DISTRIBUTION_GROUP";
    }
    if (isRemovingSingleUser) {
      return "REMOVING_USER";
    }
  }, [
    isRemovingOnlyMe,
    isRemovingSingleDistributionGroup,
    isRemovingSingleExternalUser,
    isRemovingSingleUser,
  ]);

  const executeDryRun = useCallback(async () => {
    setError(undefined);
    setDryRunOutput(undefined);
    setDisplayType("LOADING");
    setIsDryRunMismatch(false);
    try {
      const response = await removeFromMessageThreadDryRunQueryAPI({
        variables: {
          messageThreadId,
          input: {
            distributionGroupIdListToRemove,
            userIdListToRemove,
          },
        },
      });
      if (response.data) {
        setDisplayType("MODAL");
        setDryRunOutput(response.data.removeFromMessageThreadDryRun);
      }
    } catch (error: any) {
      switch (error.message) {
        case "REMOVE_FROM_MESSAGE_THREAD_DRY_RUN_MISMATCH": {
          setError("");
          setIsDryRunMismatch(true);
          break;
        }
        case "REMOVE_FROM_MESSAGE_THREAD_CANNOT_REMOVE_ALL_USERS": {
          if (isRemovingOnlyMe) {
            setError(
              <Text color={colors.gray50}>
                You cannot leave this thread because it would result in a thread
                without any participants.
              </Text>,
            );
          } else if (isRemovingSingleDistributionGroup && distributionGroup) {
            setError(
              <Text color={colors.gray50}>
                The group{" "}
                <Text as="span" fontWeight={700}>
                  {getDistributionGroupMention(distributionGroup)}
                </Text>{" "}
                cannot be removed from this thread because it would result in a
                thread without any participants.
              </Text>,
            );
          } else if (
            (isRemovingSingleUser || isRemovingSingleExternalUser) &&
            user
          ) {
            setError(
              <Text color={colors.gray50}>
                The user{" "}
                <Text as="span" fontWeight={700}>
                  {getUserMention(user)}
                </Text>{" "}
                cannot be removed from this thread because it would result in a
                thread without any participants.
              </Text>,
            );
          }
          break;
        }
        case "REMOVE_FROM_MESSAGE_THREAD_NOT_REMOVING_ANYTHING": {
          setError("");
          break;
        }
        default: {
          setError("");
          break;
        }
      }
      setDisplayType("ERROR");
    }
  }, [
    distributionGroup,
    distributionGroupIdListToRemove,
    isRemovingOnlyMe,
    isRemovingSingleDistributionGroup,
    isRemovingSingleExternalUser,
    isRemovingSingleUser,
    messageThreadId,
    removeFromMessageThreadDryRunQueryAPI,
    user,
    userIdListToRemove,
  ]);

  const onRemoveClicked = useCallback(async () => {
    if (!dryRunOutput) return;
    try {
      await removeFromMessageThreadDryRunMutationAPI({
        variables: {
          messageThreadId,
          dryRunInput: {
            distributionGroupIdListToRemove,
            userIdListToRemove,
          },
          input: {
            removedUserIdList: dryRunOutput.removedUserIdList,
            removedDistributionGroupIdList:
              dryRunOutput.removedDistributionGroupIdList,
            remainingUserIdList: dryRunOutput.remainingUserIdList,
            remainingDistributionGroupIdList:
              dryRunOutput.remainingDistributionGroupIdList,
          },
        },
      });
      if (userIdListToRemove.length > 0) {
        track(MIXPANEL_EVENT_TYPE.USER_REMOVED);
      }
      if (distributionGroupIdListToRemove.length > 0) {
        track(MIXPANEL_EVENT_TYPE.DISTRIBUTION_GROUP_REMOVED);
      }
      closeModal();
    } catch (error: any) {
      switch (error.message) {
        case "REMOVE_FROM_MESSAGE_THREAD_DRY_RUN_MISMATCH": {
          setError(undefined);
          setIsDryRunMismatch(true);
          break;
        }
        default: {
          setError(undefined);
          break;
        }
      }
    }
  }, [
    closeModal,
    distributionGroupIdListToRemove,
    dryRunOutput,
    messageThreadId,
    removeFromMessageThreadDryRunMutationAPI,
    track,
    userIdListToRemove,
  ]);

  const warningList = useMemo(() => {
    if (!dryRunOutput) return null;
    return (
      <UnorderedList pl={4}>
        {actionType === "REMOVING_DISTRIBUTION_GROUP" && (
          <ListItem>
            <Text fontSize={12} color={colors.gray50}>
              Any group members individually called out in this thread must be
              removed separately.
            </Text>
          </ListItem>
        )}
        {willRemoveExternalContacts && (
          <ListItem>
            <Text fontSize={12} color={colors.gray50}>
              Removed external contacts will no longer receive emails when
              messages are added to the thread until the email is added again.
            </Text>
          </ListItem>
        )}
        {willRemoveAdditionalGroups && (
          <ListItem>
            <Text fontSize={12} color={colors.gray50}>
              <Text fontWeight={700} as="span">
                {user && getUserMention(user)}
              </Text>{" "}
              cannot be removed unless the group
              {dryRunOutput.removedDistributionGroupIdList.length > 1
                ? "s "
                : " "}
              {getDistributionGroupsById(
                dryRunOutput.removedDistributionGroupIdList,
              ).map((distributionGroup, index) => (
                <Text key={distributionGroup.id} as="span" fontWeight={700}>
                  {getDistributionGroupMention(distributionGroup)}
                  {index <
                  dryRunOutput.removedDistributionGroupIdList.length - 1
                    ? ", "
                    : " "}
                </Text>
              ))}
              {dryRunOutput.removedDistributionGroupIdList.length === 1
                ? "is "
                : "are "}
              also removed.
            </Text>
          </ListItem>
        )}
        {willRemoveMe && actionType !== "REMOVING_ME" && (
          <ListItem backgroundColor={colors.redLight}>
            <Text fontSize={12} color={colors.red} fontWeight={700}>
              Warning: you will also be removed from this thread.
            </Text>
          </ListItem>
        )}
      </UnorderedList>
    );
  }, [
    actionType,
    dryRunOutput,
    getDistributionGroupsById,
    user,
    willRemoveAdditionalGroups,
    willRemoveExternalContacts,
    willRemoveMe,
  ]);

  const dryRunMismatchWarning = useMemo(() => {
    if (!dryRunOutput) return null;
    if (!isDryRunMismatch) return null;
    return (
      <HStack w="full" backgroundColor={colors.yellow20} p={1} borderRadius={4}>
        <InfoIcon fill={colors.yellowDark} boxSize={4} />
        <Text color={colors.gray50} fontSize={12}>
          Some group details have recently been modified.
        </Text>
        <Button
          variant="link"
          color={colors.blue}
          fontSize={12}
          onClick={executeDryRun}
          iconSpacing={1}
          leftIcon={<RefreshIcon fill={colors.blue} boxSize={4} />}
        >
          Refresh
        </Button>
      </HStack>
    );
  }, [dryRunOutput, executeDryRun, isDryRunMismatch]);

  const header = useMemo(() => {
    switch (actionType) {
      case "REMOVING_ME":
        return "Leave this Thread?";
      case "REMOVING_EXTERNAL_USER":
        return "Remove External Contact?";
      case "REMOVING_DISTRIBUTION_GROUP":
        return "Remove Group?";
      case "REMOVING_USER":
        return "Remove User?";
      default:
        return "";
        break;
    }
  }, [actionType]);

  return (
    <Modal isOpen onClose={closeModal} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        {displayType === "LOADING" && (
          <ModalBody>
            <ModalCloseButton />
            <Center>
              <Spinner />
            </Center>
          </ModalBody>
        )}

        {displayType === "ERROR" && (
          <ModalBody p={10}>
            <VStack spacing={4}>
              <Text
                fontSize={20}
                fontWeight={700}
                color={colors.blackSecondary}
              >
                {header}
              </Text>
              {error}
              <Button h={50} w={28} onClick={closeModal}>
                Close
              </Button>
            </VStack>
          </ModalBody>
        )}

        {displayType === "MODAL" && (
          <ModalBody p={10}>
            {dryRunOutput && (
              <VStack spacing={4}>
                <Text
                  fontSize={20}
                  fontWeight={700}
                  color={colors.blackSecondary}
                >
                  {header}
                </Text>

                {dryRunMismatchWarning}

                {actionType === "REMOVING_ME" && (
                  <Text fontSize={14} color={colors.gray50}>
                    If you leave this thread you will not have access to new
                    messages and updates until you are added back to the thread.
                  </Text>
                )}
                {actionType === "REMOVING_EXTERNAL_USER" && (
                  <Text fontSize={14} color={colors.gray50}>
                    Are you sure you want to remove{" "}
                    <Text fontWeight={700} as="span">
                      {user && <UserName userId={user.id} />}
                    </Text>{" "}
                    from this thread?
                  </Text>
                )}
                {actionType === "REMOVING_DISTRIBUTION_GROUP" && (
                  <Text fontSize={14} color={colors.gray50}>
                    Are you sure you want to remove the group{" "}
                    {distributionGroup &&
                      getDistributionGroupMention(distributionGroup)}
                    ?
                  </Text>
                )}
                {actionType === "REMOVING_USER" && (
                  <Text fontSize={14} color={colors.gray50}>
                    Are you sure you want to remove{" "}
                    <Text fontWeight={700} as="span">
                      {user && getUserMention(user)} (
                      {user && <UserName userId={user.id} />})
                    </Text>{" "}
                    from this thread?
                  </Text>
                )}

                {warningList}

                <VStack w="full" spacing={0}>
                  <HStack w="full">
                    <PersonOutlineIcon fill={colors.gray50} />
                    <Text>
                      Remaining People in Thread (
                      {dryRunOutput.remainingUserIdList.length})
                    </Text>
                  </HStack>
                  <Divider borderColor={colors.gray30} />
                  <Box overflowY="auto" maxH={200} w="full">
                    {getUsersById(dryRunOutput.remainingUserIdList).map(
                      (user) => (
                        <HStack
                          key={user.id}
                          h="40px"
                          borderBottom={1}
                          borderBottomStyle="solid"
                          borderColor={colors.grayPrimary}
                        >
                          <UserAvatar
                            userId={user.id}
                            size="xs"
                            displayOnlineIndicator={false}
                          />
                          <Text
                            color={colors.gray50}
                            fontSize={14}
                            fontWeight={500}
                          >
                            {getUserMention(user)}
                          </Text>
                        </HStack>
                      ),
                    )}
                  </Box>
                </VStack>
                <HStack>
                  <Button variant="cancel" h={50} w={28} onClick={closeModal}>
                    Cancel
                  </Button>
                  {!error && dryRunOutput && (
                    <Button
                      height={8}
                      w={28}
                      h={50}
                      onClick={onRemoveClicked}
                      isDisabled={isDryRunMismatch}
                    >
                      {actionType === "REMOVING_ME" ? "Leave" : "Remove"}
                    </Button>
                  )}
                </HStack>
              </VStack>
            )}
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};

export default memo(ConfirmRemoveUsersGroupsModal);